import { useCallback, useEffect, useState } from 'react';

import { apiFetch } from '../../../../api/crud';
import { SHIPMENTS_TRACKERS_PATH } from '../../config/service';
import { TRACKING_DRAWER_OPEN_EVENT } from '../../config/events';
import getMockShipments, { isMockActive, registerMockGlobals, MOCK_EVENT } from './mock';
import styles from './PackageTrackingPill.module.scss';

const API_STEP_KEYS = [
  'bag_registered',
  'carrier_scan_detected',
  'sorting_center_received',
  'sorting_center_completed',
];

const STAGE_LABELS = {
  1: 'Bag\nOrdered',
  2: 'Labels\nCreated',
  3: 'Gift\u00A0card\nearned',
  4: 'Being\nSorted',
  5: 'Shipment\nCompleted!',
};

const LOST_LABEL = 'Bag\nLost';

function isShipmentLost(shipment) {
  return shipment?.steps?.some(
    s => s?.key === 'sorting_center_received' && s?.data?.isLost === true,
  ) ?? false;
}

function getCompletedCount(shipment) {
  if (!shipment) return 0;
  const apiSteps = shipment.steps ?? [];
  let count = 1;
  for (let i = 0; i < API_STEP_KEYS.length; i += 1) {
    const step = apiSteps.find(s => s?.key === API_STEP_KEYS[i]);
    if (step?.completed) {
      count = i + 2;
    }
  }
  const allDone = API_STEP_KEYS.every(
    k => apiSteps.find(s => s?.key === k)?.completed,
  );
  return allDone ? 5 : count;
}

function getBestShipment(shipments) {
  if (!Array.isArray(shipments) || shipments.length === 0) return null;
  let best = shipments[0];
  let bestCount = getCompletedCount(shipments[0]);
  for (let i = 1; i < shipments.length; i += 1) {
    const c = getCompletedCount(shipments[i]);
    if (c >= bestCount) {
      best = shipments[i];
      bestCount = c;
    }
  }
  return best;
}

const RING_STROKE_WIDTH = 3.5;

const PackageTrackingPill = ({ selectedShipmentIndex = null }) => {
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchTrackers = useCallback(async () => {
    setLoading(true);
    setError(null);
    if (isMockActive()) {
      setShipments(getMockShipments());
      setLoading(false);
      return;
    }
    try {
      const result = await apiFetch({ path: SHIPMENTS_TRACKERS_PATH });
      const raw = result?.data?.trackers
        ?? result?.trackers
        ?? result?.data
        ?? result;
      let list = [];
      if (Array.isArray(raw?.shipments)) {
        list = raw.shipments;
      } else if (Array.isArray(raw)) {
        list = raw;
      }
      setShipments(list);
    } catch (err) {
      setError(err);
      setShipments([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    registerMockGlobals();
    fetchTrackers();
  }, [fetchTrackers]);

  useEffect(() => {
    const onMockChange = () => fetchTrackers();
    window.addEventListener(MOCK_EVENT, onMockChange);
    return () => window.removeEventListener(MOCK_EVENT, onMockChange);
  }, [fetchTrackers]);

  const numericSelectedIndex = Number(selectedShipmentIndex);
  const hasSelectedIndex = Number.isFinite(numericSelectedIndex);
  const normalizedSelectedIndex = hasSelectedIndex
    ? Math.max(0, Math.min(shipments.length - 1, numericSelectedIndex))
    : null;
  const display = normalizedSelectedIndex !== null
    ? shipments[normalizedSelectedIndex]
    : getBestShipment(shipments);
  const completedCount = display ? getCompletedCount(display) : 0;
  const lost = display ? isShipmentLost(display) : false;
  const label = lost
    ? LOST_LABEL
    : (STAGE_LABELS[completedCount] || STAGE_LABELS[1]);

  const R = 10;
  const CX = 12;
  const CY = 12;
  const NUM_BARS = 5;
  const GAP_DEG = 28;
  const SEG_DEG = (360 - NUM_BARS * GAP_DEG) / NUM_BARS;
  const START_DEG = -90 + GAP_DEG / 2;

  const arcSegments = [];
  for (let i = 0; i < NUM_BARS; i += 1) {
    const a1 = START_DEG + i * (SEG_DEG + GAP_DEG);
    const a2 = a1 + SEG_DEG;
    const r1 = (a1 * Math.PI) / 180;
    const r2 = (a2 * Math.PI) / 180;
    const x1 = CX + R * Math.cos(r1);
    const y1 = CY + R * Math.sin(r1);
    const x2 = CX + R * Math.cos(r2);
    const y2 = CY + R * Math.sin(r2);
    const filled = i < (completedCount || 0);
    arcSegments.push({ id: `arc-${a1}-${a2}`, x1, y1, x2, y2, filled });
  }

  if (loading || error || shipments.length === 0) {
    return null;
  }

  const pillClass = lost
    ? `${styles.pill} ${styles.lost}`
    : styles.pill;

  return (
    <button
      type="button"
      className={pillClass}
      aria-label="Package tracking – view details"
      onClick={() => window.dispatchEvent(new Event(TRACKING_DRAWER_OPEN_EVENT))}
    >
      <div className={styles.iconWrap}>
        <svg
          className={styles.iconSvg}
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden
        >
          {arcSegments.map((seg) => (
            <path
              key={seg.id}
              d={`M ${seg.x1} ${seg.y1} A ${R} ${R} 0 0 1 ${seg.x2} ${seg.y2}`}
              fill="none"
              stroke="currentColor"
              strokeWidth={RING_STROKE_WIDTH}
              strokeLinecap="round"
              className={seg.filled ? styles.ringProgress : styles.ring}
            />
          ))}
          <g
            className={styles.trackingIcon}
            transform="translate(12,12) scale(0.722) translate(-8,-8)"
          >
            <rect
              x="1.95898"
              y="2.03198"
              width="12.0026"
              height="12.0026"
              rx="2.5"
              stroke="currentColor"
            />
            <line
              x1="1.87988"
              y1="3.76807"
              x2="13.9222"
              y2="3.76807"
              stroke="currentColor"
            />
            <path
              d="M10.4011 6.70557C10.6365 6.70562 10.8271 6.89684 10.8271 7.1322C10.8269 7.36743 10.6364 7.55808 10.4011 7.55814H5.6041C5.36893 7.55799 5.17831 7.36737 5.17816 7.1322C5.17816 6.8969 5.36884 6.70572 5.6041 6.70557H10.4011Z"
              fill="currentColor"
            />
            <path
              d="M12.2277 8.49854C12.463 8.49859 12.6536 8.68981 12.6536 8.92517C12.6535 9.1604 12.4629 9.35105 12.2277 9.35111H3.77756C3.54239 9.35096 3.35177 9.16034 3.35162 8.92517C3.35162 8.68987 3.5423 8.49869 3.77756 8.49854H12.2277Z"
              fill="currentColor"
            />
            <path
              d="M10.4011 10.2913C10.6365 10.2913 10.8271 10.4825 10.8271 10.7179C10.8269 10.9531 10.6364 11.1438 10.4011 11.1438H5.6041C5.36893 11.1437 5.17831 10.9531 5.17816 10.7179C5.17816 10.4826 5.36884 10.2914 5.6041 10.2913H10.4011Z"
              fill="currentColor"
            />
          </g>
        </svg>
      </div>
      <span className={styles.label}>{label}</span>
    </button>
  );
};

// eslint-disable-next-line import/prefer-default-export
export { PackageTrackingPill };
